/**
 * Every call with this lead, on the lead's own page.
 *
 * The gap this closes is small to describe and expensive in practice: two
 * operators working the same board could each ring the same company on the same
 * day, because nothing on the record said the first call had happened. The call
 * log knew; the lead page did not ask.
 *
 * Asked through the calls module's own list endpoint rather than folded into
 * `GET /leads/:id`, so it inherits the calls permission for free. The lead page
 * is `view:crm`, and what was said on a call is deliberately not.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PhoneIncoming, PhoneMissed, PhoneOff, PhoneOutgoing, Voicemail } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { callCounterpart, listCalls, type CallRow } from "../../../lib/calls";
import { formatPhone } from "../../../lib/phone";
import { fmtDuration, fmtStamp } from "../../ui";

/** Enough to see the pattern without turning the lead page into a call list. */
const SHOWN = 10;

/**
 * The same five states the call list draws, read the same way.
 *
 * The blocked branch is FIRST after the voicemail, and on this page it is the
 * one that earns its place. A call the blocklist refused would otherwise draw
 * here as an ordinary incoming call, so the lead's own record would say they
 * rang us and say nothing about us hanging up on them. That is the exact
 * mistake the blocklist screen warns about, on the one page where somebody
 * would be reading about this lead anyway.
 */
function CallIcon({ call }: { call: CallRow }) {
  if (call.voicemail) return <Voicemail className="size-3.5 text-amber-500" />;
  if (call.status === "blocked") {
    return <PhoneOff className="size-3.5 text-muted-foreground" />;
  }
  if (call.status === "no-answer" || call.status === "busy" || call.status === "failed") {
    return <PhoneMissed className="size-3.5 text-rose-500" />;
  }
  return call.direction === "inbound" ? (
    <PhoneIncoming className="size-3.5 text-sky-500" />
  ) : (
    <PhoneOutgoing className="size-3.5 text-emerald-600" />
  );
}

export function LeadCalls({ leadId }: { leadId: string }) {
  const t = useT();
  const [rows, setRows] = useState<CallRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    listCalls({ leadId, pageSize: SHOWN })
      .then((res) => {
        if (alive) setRows(res.rows);
      })
      // An operator without the calls permission gets a 403 here, and the right
      // answer to that is a card that is not drawn rather than an error on a
      // page about something else.
      .catch(() => {
        if (alive) setRows([]);
      });
    return () => {
      alive = false;
    };
  }, [leadId]);

  if (!rows || rows.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-background p-5">
      <h2 className="mb-3 text-sm font-semibold">{t("leads.calls")}</h2>
      <ul className="space-y-1.5">
        {rows.map((call) => (
          <li key={call.id}>
            <Link
              to={`/calls/${call.id}`}
              className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-accent"
            >
              <CallIcon call={call} />
              <span className="min-w-0 flex-1 truncate">
                {callCounterpart(call) ? formatPhone(callCounterpart(call)) : t("calls.incoming.withheld")}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {call.durationSec ? fmtDuration(call.durationSec) : "-"}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">{fmtStamp(call.startedAt)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
